import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import AZIENDA from '../../config/azienda';
import { destinazioneDopoAccesso } from '../../context/AuthContext';
import LogoAnimato from './LogoAnimato';
import { classi, IconaRapido, nomeSistema, TestaCard, useInvioUnico } from './campi';
import { momentoDelGiorno, SALUTI } from '../../utils/percorso';
import { FornitoreLingua, useTraduzioni } from '../../i18n';
import { useLinguaOspite } from '../../utils/linguaOspite';
import {
  passkeySupportate, piattaforma, dispositivoConPasskey, segnaDispositivo, rimandaProposta,
  propostaRimandata, preparaRegistrazione, registraPasskey, annullato,
} from '../../utils/passkey';
import './accesso.css';

// Scena comune di login e registrazione (bozza design/login-v10.html): stelle, logo che si disegna,
// card a destra, piede. Uso in una pagina:
//   const scena = useScenaAccesso('MK Client — Accedi');
//   ... scena.scuoti() sugli errori, scena.entra(esito) dopo un accesso riuscito ...
//   return <ScenaAccesso scena={scena}>{testa e corpo della card}</ScenaAccesso>;
// entra() propone l'accesso rapido (passkey) se il dispositivo lo permette, poi mostra lo schermo
// "Bentornati, nome" (o "Benvenuti") e porta alla pagina del ruolo.

// Tempi della sequenza, in millisecondi, come nella bozza
const ACCENSIONE_CARD = 3500;  // la card si "accende" quando il logo è completo
const USCITA_CARD = 500;       // la card esce, poi compare lo schermo scuro
const DURATA_BENVENUTO = 1600; // la linea che si riempie, poi si entra

const movimentoRidotto = () => !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// Fino alle 5 di notte è ancora sera (prima all'1 di notte diceva "Buongiorno"): la regola sta in utils/percorso.js,
// la stessa del saluto della home del cliente

// Dopo l'accesso: la pagina da cui si era partiti (?torna=, messo da ProtectedRoute: un link aperto da scollegati,
// la sessione scaduta) se è una pagina del proprio ruolo, altrimenti la pagina del ruolo (destinazioneDopoAccesso)
export const tornaDa = (search) => {
  try { return new URLSearchParams(search || '').get('torna'); } catch { return null; }
};

// Stelle su canvas che salgono piano e seguono il mouse in profondità; ferme con movimento ridotto
const useStelle = (ref, ridotto) => {
  useEffect(() => {
    const cv = ref.current;
    const ctx = cv?.getContext?.('2d');
    if (!ctx) return undefined;
    let pts = [];
    let larghezza = 0;
    let frame = 0;
    let frameMouse = 0;

    const dimensiona = () => {
      const dpr = window.devicePixelRatio || 1;
      cv.width = window.innerWidth * dpr;
      cv.height = window.innerHeight * dpr;
      // Su mobile la barra dell'indirizzo cambia solo l'altezza: le stelle restano dove sono
      if (cv.width !== larghezza) {
        larghezza = cv.width;
        pts = Array.from({ length: Math.min(110, cv.width / 16) }, () => ({
          x: Math.random() * cv.width, y: Math.random() * cv.height,
          r: Math.random() * 1.3 + 0.3, v: Math.random() * 0.12 + 0.04, a: Math.random() * 6,
        }));
      }
    };
    const disegna = () => {
      const dpr = window.devicePixelRatio || 1;
      ctx.clearRect(0, 0, cv.width, cv.height);
      pts.forEach((p) => {
        p.y -= p.v * dpr;
        p.a += 0.01;
        if (p.y < 0) p.y = cv.height;
        ctx.fillStyle = `rgba(230,200,119,${0.2 + 0.3 * Math.sin(p.a)})`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * dpr, 0, 7);
        ctx.fill();
      });
      if (!ridotto) frame = requestAnimationFrame(disegna);
    };
    const alRidimensionamento = () => { dimensiona(); if (ridotto) disegna(); };

    dimensiona();
    disegna();
    window.addEventListener('resize', alRidimensionamento);

    let tx = 0; let ty = 0; let cx = 0; let cy = 0;
    const alMouse = (e) => { tx = e.clientX / window.innerWidth - 0.5; ty = e.clientY / window.innerHeight - 0.5; };
    const segui = () => {
      cx += (tx - cx) * 0.04;
      cy += (ty - cy) * 0.04;
      cv.style.transform = `translate(${cx * -18}px,${cy * -12}px) scale(1.04)`;
      frameMouse = requestAnimationFrame(segui);
    };
    if (!ridotto) {
      window.addEventListener('mousemove', alMouse);
      segui();
    }
    return () => {
      cancelAnimationFrame(frame);
      cancelAnimationFrame(frameMouse);
      window.removeEventListener('resize', alRidimensionamento);
      window.removeEventListener('mousemove', alMouse);
    };
  }, [ref, ridotto]);
};

// Stato della scena, da chiamare nella pagina (i gestori degli eventi usano scuoti ed entra)
const useScenaAccesso = (titolo) => {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const torna = (typeof state?.torna === 'string' && state.torna) || tornaDa(search);
  const [ora] = useState(() => momentoDelGiorno());
  const [ridotto] = useState(movimentoRidotto);
  const [entrati, setEntrati] = useState({ plan: ridotto, card: ridotto });
  const [accesa, setAccesa] = useState(ridotto);
  const [scossa, setScossa] = useState(false);
  const [uscita, setUscita] = useState(false);
  // parola ed etichetta sono chiavi del dizionario (la scena le traduce quando le mostra)
  const [benvenuto, setBenvenuto] = useState({ on: false, nome: '', parola: 'accesso.bentornati', etichetta: 'accesso.accesso_eseguito' });
  const [proposta, setProposta] = useState(null); // { esito, parola, etichetta, nota }
  // accesso rapido (passkey): supporto del dispositivo e passkey già attiva qui
  const [rapido, setRapido] = useState(() => ({ supportate: false, piattaforma: piattaforma(), dispositivo: dispositivoConPasskey() }));
  const timer = useRef([]);

  useEffect(() => {
    const precedente = document.title;
    document.title = titolo;
    const inCorso = timer.current;
    return () => { document.title = precedente; inCorso.forEach(clearTimeout); };
  }, [titolo]);

  useEffect(() => {
    let attivo = true;
    passkeySupportate().then((ok) => { if (attivo) setRapido((r) => ({ ...r, supportate: ok })); });
    return () => { attivo = false; };
  }, []);

  useEffect(() => {
    if (ridotto) return undefined;
    const t = setTimeout(() => setAccesa(true), ACCENSIONE_CARD);
    return () => clearTimeout(t);
  }, [ridotto]);

  const scuoti = useCallback(() => {
    if (ridotto) return;
    setScossa(false);
    requestAnimationFrame(() => setScossa(true));
  }, [ridotto]);

  // Schermo "Bentornati, nome" e poi la pagina del proprio ruolo
  const transizione = useCallback((esito, parola = 'accesso.bentornati', etichetta = 'accesso.accesso_eseguito') => {
    const vai = () => navigate(destinazioneDopoAccesso(torna, esito.userType), { replace: true });
    if (ridotto) { vai(); return; }
    setUscita(true);
    timer.current.push(setTimeout(() => setBenvenuto({ on: true, nome: esito.userInfo?.nome || '', parola, etichetta }), USCITA_CARD));
    timer.current.push(setTimeout(vai, USCITA_CARD + DURATA_BENVENUTO));
  }, [navigate, ridotto, torna]);

  // Accesso riuscito (login o registrazione): se il dispositivo lo permette, prima l'accesso rapido
  const entra = (esito, { proponi = true, parola = 'accesso.bentornati', etichetta = 'accesso.accesso_eseguito' } = {}) => {
    const email = (esito.userInfo?.email || '').toLowerCase();
    const giaAttivo = rapido.dispositivo?.email === email;
    if (proponi && rapido.supportate && !giaAttivo && !propostaRimandata()) {
      setProposta({ esito, parola, etichetta, nota: '', opzioni: preparaRegistrazione().catch(() => null) });
      return;
    }
    transizione(esito, parola, etichetta);
  };

  return {
    ora, saluto: SALUTI[ora], ridotto, entrati, setEntrati, accesa, scossa, setScossa, uscita, benvenuto,
    proposta, setProposta, rapido, setRapido, timer, scuoti, transizione, entra, torna,
  };
};

// Scheda "Volete entrare con Face ID la prossima volta?" al posto del contenuto della card
const PropostaAccessoRapido = ({ scena }) => {
  const { proposta, setProposta, rapido, transizione, timer } = scena;
  const { t } = useTraduzioni();
  const [attesa, setAttesa] = useState(false);
  const opzioni = useRef(null);
  const invio = useInvioUnico(); // Invio due volte sul bottone: una registrazione sola (prima partivano due)
  const { esito, parola, etichetta } = proposta;

  // opzioni chieste appena compare la scheda: Safari vuole Face ID subito dal tocco, senza attese
  useEffect(() => {
    let attivo = true;
    proposta.opzioni.then((o) => { if (attivo) opzioni.current = o; });
    return () => { attivo = false; };
  }, [proposta.opzioni]);

  // si resta bloccati: dopo si entra (anche con Face ID annullato), la scheda sta uscendo
  const attiva = async () => {
    if (!invio.blocca()) return;
    const email = esito.userInfo?.email;
    setAttesa(true);
    try {
      await registraPasskey(opzioni.current, email);
    } catch (err) {
      if (err?.name === 'InvalidStateError') {
        // passkey già presente su questo dispositivo per questo utente
        segnaDispositivo({ email: (email || '').toLowerCase() });
      } else if (!annullato(err)) {
        setProposta({ ...proposta, nota: t('accesso.proposta_non_riuscita') });
        timer.current.push(setTimeout(() => transizione(esito, parola, etichetta), 2200));
        return;
      }
    }
    transizione(esito, parola, etichetta);
  };

  const nonOra = () => {
    if (!invio.blocca()) return;
    rimandaProposta();
    transizione(esito, parola, etichetta);
  };

  return (
    <div className="scheda-nuova">
      <TestaCard etichetta={t('accesso.rapido')} titolo={t('accesso.proposta_titolo')}
        corsivo={t('accesso.con_sistema', { sistema: nomeSistema(rapido.piattaforma.sistema, t) })} dopo={t('accesso.proposta_dopo')}
        sotto={t('accesso.proposta_sotto')} />
      <div className="body">
        {proposta.nota && <p className="nota">{proposta.nota}</p>}
        <button type="button" className={classi('btn', 'rapido', attesa && 'busy')} aria-busy={attesa}
          onClick={attiva} disabled={attesa || !!proposta.nota} data-testid="passkey-attiva">
          <IconaRapido tipo={rapido.piattaforma.icona} /><span>{t('accesso.attiva')}</span>
        </button>
        <button type="button" className="btn sec" onClick={nonOra} disabled={attesa || !!proposta.nota} data-testid="passkey-non-ora">
          <span>{t('accesso.non_ora')}</span>
        </button>
      </div>
    </div>
  );
};

const Scena = ({ scena, children }) => {
  const stelle = useRef(null);
  const { t } = useTraduzioni();
  // privacy e termini sanno da dove si arriva ("Torna alla registrazione"; prima sempre al login)
  const { pathname, search } = useLocation();
  const provenienza = { da: `${pathname}${search}` };
  useStelle(stelle, scena.ridotto);
  const { entrati, setEntrati, accesa, scossa, setScossa, uscita, benvenuto, proposta } = scena;

  // A entrata finita la classe .in va tolta: altrimenti la sua animazione blocca scossone e uscita
  const fineAnimazione = (chi) => (e) => {
    if (e.target !== e.currentTarget) return;
    if (e.animationName === 'mkl-up') setEntrati((s) => ({ ...s, [chi]: true }));
    if (e.animationName === 'mkl-shake') setScossa(false);
  };

  return (
    <div className={classi('mk-accesso', uscita && 'leaving')} data-ora={scena.ora}>
      <canvas ref={stelle} className="stars" aria-hidden="true" />
      <div className="glow" />
      <div className="grain" />

      <div className="top">
        <a className="brand" href={AZIENDA.sito}>
          <img src="/mk_logo_original.png" alt="" />MK<i>.</i>Consulting<span>Client</span>
        </a>
      </div>

      <main className="stage">
        <div className={classi('plan', !entrati.plan && 'in')} onAnimationEnd={fineAnimazione('plan')}>
          <LogoAnimato />
          <div className="cap">
            <span>{t('accesso.claim')} <b>{t('accesso.claim_forte')}</b></span>
            <span>{AZIENDA.citta} <b>({AZIENDA.provincia})</b></span>
          </div>
        </div>

        <div className={classi('card', !entrati.card && 'in d1', accesa && 'lit', scossa && 'shake')} onAnimationEnd={fineAnimazione('card')}>
          {proposta ? <PropostaAccessoRapido scena={scena} /> : children}
          <div className="legal">
            <Link to="/privacy" state={provenienza}>{t('legali.privacy_breve')}</Link>
            <Link to="/termini" state={provenienza}>{t('legali.termini')}</Link>
          </div>
        </div>
      </main>

      <div className={classi('welcome', benvenuto.on && 'on')} aria-hidden={!benvenuto.on} role="status">
        <div className="w">
          <div className="k">{t(benvenuto.etichetta)}</div>
          <h2>{benvenuto.nome ? <>{t(benvenuto.parola)}, <em>{benvenuto.nome}</em></> : t(benvenuto.parola)}</h2>
          <div className="bar"><i /></div>
        </div>
      </div>
      <div className="foot">
        © {new Date().getFullYear()} {AZIENDA.nome} · {AZIENDA.via}, {AZIENDA.cap} {AZIENDA.citta} ({AZIENDA.provincia}) · P.IVA {AZIENDA.partitaIva} · <a href={AZIENDA.telefonoHref}>{AZIENDA.telefono}</a>
      </div>
    </div>
  );
};

// Fuori dall'area del cliente la lingua è quella del browser (o quella del profilo, se si è già entrati):
// la mette qui, così tutta la scena e i pezzi della card (campi.jsx) la trovano con useTraduzioni().
// Le pagine, che stanno sopra, la chiedono per conto loro con useTraduzioniOspite().
const ScenaAccesso = (props) => {
  const lingua = useLinguaOspite();
  return <FornitoreLingua lingua={lingua}><Scena {...props} /></FornitoreLingua>;
};

export { ScenaAccesso, useScenaAccesso, momentoDelGiorno, SALUTI };
